import * as React from 'react'

import axios from 'axios'
import { autorun } from 'mobx'

import { observer } from 'mobx-react'

import { TentacleError } from 'modules/core/lib/errors'
import type { TentacleErrorData } from 'modules/core/lib/parseErrorData'

import { useContent } from 'modules/reports/hooks/useContent'
import type ReportStruct from 'modules/reports/lib/ReportStruct'
import { ContentError } from 'modules/reports/store/models/Content'
import Report, { type Variable } from 'modules/reports/store/models/Report'
import type { ResourceData } from 'modules/reports/store/models/Resource'

const DEFAULT_SERVER_ERROR = 'SERVER_ERROR'
const DEFAULT_SERVER_STATUS_CODE = 500

interface State {
  data?: ResourceData
  error?: TentacleErrorData | Error | ContentError
  loading: boolean
}

interface FetchAction {
  type: 'fetch'
  data?: ResourceData | null
}
interface LoadAction {
  type: 'load'
  data?: ResourceData
  loading?: boolean
}
interface ErrorAction {
  error: TentacleError | ContentError
  type: 'error'
}
type Action = FetchAction | LoadAction | ErrorAction

function reducer(state: State, action: Action) {
  switch (action.type) {
    case 'fetch':
      return {
        loading: true,
        data: state.data,
        error: undefined,
      }

    case 'load':
      return {
        loading: false,
        data: action.data,
        error: state.error,
      }

    case 'error':
      return {
        loading: false,
        data: state.data,
        error:
          action.error instanceof ContentError ? action.error : action.error?.data || action.error,
      }
  }
}

type ReducerType = React.Reducer<State, Action>

export interface GeneratorRenderProps {
  data?: ResourceData
  error?: TentacleErrorData | Error
  loading: boolean
  hasTable: boolean
}

interface Props {
  reportStruct: ReportStruct
  variables: Variable[]
  lastRefreshTimeString?: string
  /** Callback called with a true if the last execution was an error, false otherwise. */
  onResult?: (error: boolean) => void
}

const Generator = ({
  reportStruct,
  variables,
  lastRefreshTimeString,
  onResult,
  children,
}: Props & React.ConsumerProps<GeneratorRenderProps>) => {
  const [{ data, loading, error }, dispatch] = React.useReducer<ReducerType>(reducer, {
    loading: true,
  })
  const { fetchReportData } = useContent()

  React.useEffect(
    () => {
      autorun(async () => {
        try {
          dispatch({
            type: 'fetch',
            data: null,
          })

          const data = await fetchReportData(reportStruct, variables)

          dispatch({
            type: 'load',
            data,
          })
          onResult?.(false)

          // FIXME: Typescript only supports exceptions at the moment
        } catch (e: unknown) {
          // We need to display the loading screen if a request was cancelled by an user
          if (e && typeof e === 'object' && 'name' in e && e.name === 'RequestCancelledError') {
            dispatch({
              type: 'fetch',
              data: null,
            })
          } else if (axios.isCancel(e)) {
          } else if (e instanceof ContentError) {
            dispatch({
              type: 'error',
              error: e,
            })
            onResult?.(true)
          } else {
            // FIXME: Get rid of this exception class when tentacle will be removed
            const errorMessage =
              e && typeof e === 'object' && 'message' in e && typeof e.message === 'string'
                ? e.message
                : ''
            const errorName =
              e && typeof e === 'object' && 'name' in e && typeof e.name === 'string' ? e.name : ''
            const tentacleError = new TentacleError({
              message: errorMessage,
              code: errorName,
              error: DEFAULT_SERVER_ERROR,
              status_code: DEFAULT_SERVER_STATUS_CODE,
            })

            dispatch({
              type: 'error',
              error: tentacleError,
            })
            onResult?.(true)
          }
        }
      })
    },
    // Adding lastRefreshTimeString here to force a content refetch when data is updated
    [reportStruct, variables, fetchReportData, lastRefreshTimeString, dispatch] // eslint-disable-line react-hooks/exhaustive-deps
  )

  function hasTable(): boolean {
    return [
      Report.CHART_TYPES.bars,
      Report.CHART_TYPES.lines,
      Report.CHART_TYPES.pie,
      Report.CHART_TYPES.area,
      Report.CHART_TYPES.scatterplot,
    ].includes(reportStruct.type)
  }

  return (
    <>
      {children({
        loading,
        data,
        error,
        hasTable: hasTable(),
      })}
    </>
  )
}

export default observer(Generator)
